import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "../../components/ReportModall.css";
import "../DashboardSkeleton.css";
import { useNotePersonalNotes, useNotesListActions } from "../../hooks/useNotesFeedback";

const CloseIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3.25 3.25L12.75 12.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M12.75 3.25L3.25 12.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const StickyNotesShimmer = () => (
  <>
    {Array.from({ length: 3 }).map((_, index) => (
      <article className="report-card" key={`note-shimmer-${index}`}>
        <div className="report-card__top">
          <div className="report-card__heading">
            <span className="dashboard-skeleton dashboard-skeleton--icon report-modal__shimmer-index" />
            <span className="dashboard-skeleton dashboard-skeleton--line report-modal__shimmer-title" />
          </div>
          <span className="dashboard-skeleton dashboard-skeleton--icon report-modal__shimmer-action" />
        </div>
        <span className="dashboard-skeleton dashboard-skeleton--line report-modal__shimmer-line" />
        <span className="dashboard-skeleton dashboard-skeleton--line report-modal__shimmer-line report-modal__shimmer-line--short" />
      </article>
    ))}
  </>
);

const NotesStickyNotesPopup = ({ open, onClose, note = null, notesQueryParams = {} }) => {
  const [noteText, setNoteText] = useState("");
  const noteId = Number(note?.id || 0);
  const notesQuery = useNotePersonalNotes(noteId, {
    enabled: open && Boolean(noteId),
  });
  const { createNote, deleteNote } = useNotesListActions(notesQueryParams);

  const notesLoading = (notesQuery.isLoading || notesQuery.isFetching) && (!notesQuery.data || notesQuery.isPlaceholderData);

  useEffect(() => {
    if (!open) {
      setNoteText("");
    }
  }, [open, noteId]);

  if (!open) return null;

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    onClose?.();
  };

  const handleSave = async () => {
    if (!noteText.trim()) return;

    await createNote.mutateAsync({
      notes_id: noteId,
      message: noteText.trim(),
    });
    setNoteText("");
  };

  const handleDeleteNote = async (item) => {
    const personalNoteId = Number(item?.id || 0);
    if (!personalNoteId || deleteNote.isPending) {
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Sticky Note?",
      text: "This note will be removed permanently for this note item.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#e53935",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await deleteNote.mutateAsync({
        note_id: personalNoteId,
        notes_id: noteId,
      });

      await Swal.fire({
        icon: "success",
        title: "Note Deleted",
        text: "Your sticky note has been removed successfully.",
      });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Unable to Delete Note",
        text: error?.response?.data?.message || error?.message || "Please try again.",
      });
    }
  };

  return (
    <div className="report-modal-overlay" role="presentation" onClick={handleClose}>
      <div className="report-modal" role="dialog" aria-modal="true" aria-label="Sticky Notes" onClick={(event) => event.stopPropagation()}>
        <span className="filter-blog-modal__corner filter-blog-modal__corner--top" />
        <span className="filter-blog-modal__corner filter-blog-modal__corner--bottom" />
        <button type="button" className="report-modal__close" onClick={handleClose} aria-label="Close sticky notes modal">
          <CloseIcon />
        </button>

        <div className="report-modal__header">
          <div className="report-modal__title-group">
            <div className="report-modal__icon" aria-hidden="true">
              <i className="fa-solid fa-file-lines"></i>
            </div>
            <h3 className="report-modal__title">Sticky Notes</h3>
          </div>
        </div>

        <div className="report-modal__body">
          <div className="report-modal__list" aria-label="Sticky notes">
            {notesLoading ? <StickyNotesShimmer /> : null}
            {!notesLoading && notesQuery.error ? (
              <div className="choose-state__status choose-state__status--error">
                {notesQuery.error?.response?.data?.message || notesQuery.error?.message || "Unable to load notes."}
              </div>
            ) : null}
            {!notesLoading && !notesQuery.error && !(notesQuery.data?.items || []).length ? (
              <div className="choose-state__status">No sticky notes available.</div>
            ) : null}
            {(notesQuery.data?.items || []).map((item, index) => (
              <article className="report-card" key={item.id}>
                <div className="report-card__top">
                  <div className="report-card__heading">
                    <span className="report-card__index">{String(index + 1).padStart(2, "0")}</span>
                    <h4 className="report-card__title">Your Sticky</h4>
                  </div>
                  <button type="button" className="report-card__dismiss" onClick={() => handleDeleteNote(item)} disabled={deleteNote.isPending} aria-label="Delete note">
                    <i className="bi bi-trash3" aria-hidden="true" />
                  </button>
                </div>
                <p className="report-card__text">{item.description}</p>
              </article>
            ))}
          </div>

          <div className="report-modal__compose">
            <div className="report-modal__compose-label sticky-label">
              <i className="bi bi-pencil-square"></i>
              <span>Add Personal Note</span>
            </div>
            <textarea className="report-modal__textarea" placeholder="Add Personal Note" value={noteText} onChange={(event) => setNoteText(event.target.value)} />
          </div>
        </div>

        <div className="report-modal__footer">
          <button type="button" className="report-modal__save" onClick={handleSave} disabled={!noteText.trim() || createNote.isPending}>
            {createNote.isPending ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotesStickyNotesPopup;
